import React from 'react';
import type { ReactInstance } from 'react';
import warning from 'warning';

import type { Nullable } from '../typings/Types.js';
import { ReactUiDetection, getRootNode } from './ReactUiDetection.js';
import { smoothScrollIntoView } from './smoothScrollIntoView.js';
import { isElement } from './utils/utils.js';
import { ValidationContext } from './ValidationContextWrapper.js';
import type { ValidationContextType } from './ValidationContextWrapper.js';
import { getIndependent, getLevel, getType, getVisibleValidation, isEqual } from './ValidationHelper.js';

export type ValidationBehaviour = 'immediate' | 'lostfocus' | 'submit';

export type ValidationLevel = 'error' | 'warning';

export type TextPosition = 'bottom' | 'right';

export interface Validation {
  level: ValidationLevel;
  behaviour: ValidationBehaviour;
  message: React.ReactNode;
  independent: boolean;
}

export type RenderErrorMessage = (
  control: React.ReactElement<any>,
  hasError: boolean,
  validation: Nullable<Validation>,
) => React.ReactElement<any>;

export interface ValidationWrapperInternalProps {
  children?: React.ReactElement<any>;
  validation: Nullable<Validation>;
  errorMessage: RenderErrorMessage;
  'data-tid'?: string;
}

interface ValidationWrapperInternalState {
  validation: Nullable<Validation>;
}

interface Point {
  x: number;
  y: number;
}

export class ValidationWrapperInternal extends React.Component<
  ValidationWrapperInternalProps,
  ValidationWrapperInternalState
> {
  public state: ValidationWrapperInternalState = {
    validation: null,
  };

  public isChanging = false;
  private child: any; // todo type
  private rootNode: Nullable<Element>;

  public static contextType = ValidationContext;
  public context: ValidationContextType = this.context;

  public componentDidMount() {
    warning(
      this.context,
      'ValidationWrapper should appears as child of ValidationContainer.\n' +
        'https://tech.skbkontur.ru/kontur-ui/?path=/docs/react-ui-validations_displaying-getting-started--docs',
    );
    if (this.context) {
      this.context.register(this);
    }
    if (this.context.isValidationCalled()) {
      this.processSubmit();
    } else {
      this.applyValidation(this.props.validation);
    }
  }

  public componentWillUnmount() {
    this.context.unregister(this);
  }

  public componentDidUpdate() {
    this.applyValidation(this.props.validation);
  }

  public async focus(): Promise<void> {
    const htmlElement = this.getRootNode();
    if (isElement(htmlElement)) {
      const { disableSmoothScroll, scrollOffset } = this.context.getSettings();
      if (!disableSmoothScroll) {
        await smoothScrollIntoView(htmlElement, scrollOffset);
      }
      if (this.child && typeof this.child.focus === 'function') {
        this.child.focus({ withoutOpenDropdown: true });
      }
    }
    this.isChanging = false;
  }

  public render(): React.JSX.Element {
    const { children, 'data-tid': dataTid } = this.props;
    const { validation } = this.state;

    let clonedChild: React.ReactElement<any> = children ? (
      React.cloneElement(children, {
        ref: this.customRef,
        error: !this.isChanging && getLevel(validation) === 'error',
        warning: !this.isChanging && getLevel(validation) === 'warning',
        onBlur: (...args: any[]) => {
          this.handleBlur();
          if (children.props && children.props.onBlur) {
            children.props.onBlur(...args);
          }
        },
        onChange: (...args: any[]) => {
          this.isChanging = true;
          if (children.props && children.props.onChange) {
            children.props.onChange(...args);
          }
        },
        onValueChange: (...args: any[]) => {
          this.isChanging = true;

          if (ReactUiDetection.isSelectionControl(children)) {
            this.setValidation(null);
          }

          if (children.props && children.props.onValueChange) {
            children.props.onValueChange(...args);
          }
        },
      })
    ) : (
      <span ref={this.setRootNode} />
    );
    if (ReactUiDetection.isComboBox(clonedChild) || ReactUiDetection.isTimePicker(clonedChild)) {
      clonedChild = React.cloneElement(clonedChild, {
        onInputValueChange: (...args: any[]) => {
          this.isChanging = true;
          this.forceUpdate();
          if (children && children.props && children.props.onInputValueChange) {
            return children.props.onInputValueChange(...args);
          }
        },
      });
    }

    const errorMessage = this.props.errorMessage(
      <div style={{ display: 'inline' }}>{clonedChild}</div>,
      !!validation,
      validation,
    );

    return React.cloneElement(errorMessage, {
      'data-tid': dataTid,
    });
  }

  private customRef = (instance: Nullable<ReactInstance>) => {
    const { children } = this.props;

    this.setRootNode(instance);
    const child = children as any; // todo type or maybe React.Children.only
    if (child && child.ref) {
      if (typeof child.ref === 'function') {
        child.ref(instance);
      }
      if (Object.prototype.hasOwnProperty.call(child.ref, 'current')) {
        child.ref.current = instance;
      }
    }
    this.child = instance;
  };

  private setRootNode = (element: Nullable<ReactInstance>) => {
    this.rootNode = getRootNode(element);
  };

  public getRootNode = (): Nullable<Element> => {
    return this.rootNode;
  };

  public getControlPosition(): Nullable<Point> {
    const htmlElement = this.getRootNode();
    if (isElement(htmlElement)) {
      const rect = htmlElement.getBoundingClientRect();
      return { x: rect.top, y: rect.left };
    }
    return null;
  }

  public processBlur(): Promise<void> {
    const touched = this.isChanging;
    this.isChanging = false;
    const validation = this.getOnBlurValidation(touched);
    return this.setValidation(validation);
  }

  public async processSubmit(): Promise<void> {
    this.isChanging = false;
    return this.setValidation(this.props.validation);
  }

  public hasError(): boolean {
    return getLevel(this.state.validation) === 'error';
  }

  public hasWarning(): boolean {
    return getLevel(this.state.validation) === 'warning';
  }

  public isIndependent(): boolean {
    return getIndependent(this.state.validation || this.props.validation) === true;
  }

  private handleBlur() {
    setTimeout(() => {
      this.processBlur();
      if (!this.isIndependent()) {
        this.context.instanceProcessBlur(this);
      }
      this.setState({});
    });
  }

  private applyValidation(actual: Nullable<Validation>) {
    const visible = this.getVisibleValidation(actual);
    this.setValidation(visible);
  }

  private setValidation(validation: Nullable<Validation>): Promise<void> {
    const current = this.state.validation;

    if (current === validation) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.setState({ validation }, () => {
        if (Boolean(current) !== Boolean(validation)) {
          this.context.onValidationUpdated(this, !validation);
        }
        resolve();
      });
    });
  }

  private getOnBlurValidation(touched: boolean): Nullable<Validation> {
    const actual = this.props.validation;
    if (getType(actual) === 'submit') {
      const visible = this.state.validation;
      return !touched && getType(visible) === 'submit' ? visible : null;
    }
    return actual;
  }

  private getVisibleValidation(actual: Nullable<Validation>): Nullable<Validation> {
    const visible = this.state.validation;
    if (isEqual(visible, actual)) {
      return visible;
    }
    const changing = this.context.isAnyWrapperInChangingMode();
    return getVisibleValidation(visible, actual, changing);
  }
}
